/**
 * DM W8-2 (plan c6412 K5) — a dm.plan's permission bundle: `data.permissions[]`, granted (all or a selection) when a
 * person approves the plan revision from the inbox. Only wave-scopable grant types: the plan's parent wave is the
 * scope item, so the grant ends with the wave (sweep) or at the 14-day ceiling. W8b-2: `merge` is the wave merge
 * grant (no head — the server spends it per slice PR); a command_once grant names a command sha, not expressible here.
 */
import { z } from 'zod';
export const DM_PLAN_PERMISSIONS_FIELD = 'permissions';
export const DM_PLAN_PERMISSION_GRANT_TYPES = ['command_class', 'sudo', 'model_override', 'merge'];
/** `wave` = until the work unit ends (≤ 14 d); `once` = one use. */
export const DM_PLAN_PERMISSION_TTLS = ['wave', 'once'];
export const DM_PLAN_PERMISSIONS_MAX = 50;
const keySchema = z.string().regex(/^[a-z0-9][a-z0-9_.-]{0,63}$/, 'a lowercase key (a-z 0-9 _ . -, ≤64)');
const policyKeySchema = z.string().regex(/^[a-z0-9][a-z0-9_.:-]{0,127}$/, 'a policy key');
export const dmPlanPermissionSchema = z
    .object({
    key: keySchema,
    grant_type: z.enum(DM_PLAN_PERMISSION_GRANT_TYPES),
    scope: z
        .object({ slice_key: keySchema.optional(), policy_key: policyKeySchema.optional(), command_class: keySchema.optional(), model: keySchema.optional() })
        .strict(),
    ttl: z.enum(DM_PLAN_PERMISSION_TTLS),
    effect: z.string().min(1).max(500),
    required: z.boolean().optional(),
})
    .strict()
    .superRefine((p, ctx) => {
    const need = {
        command_class: ['policy_key', 'command_class'],
        sudo: ['command_class'],
        model_override: ['model'],
        merge: [],
    };
    for (const field of need[p.grant_type]) {
        if (p.scope[field] === undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scope', field], message: `a ${p.grant_type} permission names scope.${field}` });
        }
    }
    // The wave is the plan's parent and the head is the PR's: a field here would read as a narrowing the grant lacks.
    if (p.grant_type === 'merge') {
        for (const field of ['policy_key', 'command_class', 'model']) {
            if (p.scope[field] !== undefined) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scope', field], message: 'a merge permission names no scope field' });
            }
        }
    }
});
export const dmPlanPermissionsSchema = z
    .array(dmPlanPermissionSchema)
    .max(DM_PLAN_PERMISSIONS_MAX)
    .superRefine((list, ctx) => {
    const keys = list.map((p) => p.key);
    if (new Set(keys).size !== keys.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'permission keys must be unique within a plan' });
    }
});
//# sourceMappingURL=dmPlanPermissions.js.map